/**
 * PendingTrips.jsx
 * -----------------
 * Lists all pending car-pool requests for the current user, split into two tabs:
 *   – trips where the user is the driver
 *   – trips where the user is a passenger
 *
 * Each list item offers a "More Details" pop-up that fetches extra information
 * from the backend. An animated fallback card is shown when no results exist.
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import "./PendingTripsScreen.css";
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL } from '../../index';
import { formatTime, formatDate, formatShortDate, formatCompactDateTime } from '../../utils/dateTimeUtils';
import {
  IonContent,
  IonPage,
  IonButton,
  IonIcon,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonCard,
  IonCardContent,
  IonList,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonText,
  IonSpinner,
  IonAlert,
  IonLoading,
  IonNote,
  IonAvatar
} from '@ionic/react';
import {
  arrowBackOutline,
  timeOutline,
  closeOutline,
  informationCircleOutline,
  checkmarkOutline,
  closeCircleOutline,
  copyOutline,
  shield,
  arrowBack,
  alertCircleOutline,
  navigateOutline
} from 'ionicons/icons';

const PendingTrips = () => {
  const [selectedTab, setSelectedTab] = useState('driver');
  const [showDetailsModal, setShowDetailsModal] = useState(false);   // renamed for consistency
  const [popupTripDetails, setPopupTripDetails] = useState(null);    // holds data for modal
  const [optimizeTripsResponse, setOptimizeTripsResponse] = useState(null); // stores response from /optimizetrips API
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoadingDecision, setIsLoadingDecision] = useState(false);
  const [isLoadingRiderDecision, setIsLoadingRiderDecision] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { pendingTripResultsRider, pendingTripResultsDriver } = location.state;

  console.log("Pending Trip Results Driver:", pendingTripResultsDriver);
  console.log("Pending Trip Results Rider:", pendingTripResultsRider);

  // Derive filtered lists to avoid placeholder rows
  const validDriverTrips = (pendingTripResultsDriver?.Pending_Trips || []).filter(
    (t) => t && t.pendingReqId // simple sanity check
  );
  // Handle new nested JSON format for pendingTripRequestsRider
  const validPassengerTrips = (() => {
    // Try the new nested format first
    if (pendingTripResultsRider?.data?.pendingSeatRequests) {
      return pendingTripResultsRider.data.pendingSeatRequests.filter(t => t && t.pendingReqId);
    }
    // Fallback to the old format if the new structure doesn't exist
    if (Array.isArray(pendingTripResultsRider)) {
      return pendingTripResultsRider.filter(t => t && t.pendingReqId);
    }
    // Return empty array if neither format is valid
    return [];
  })();

  // Simple namespaced logger – disabled in production builds
  const logger = {
    info: (msg, data) => process.env.NODE_ENV !== 'production' && console.info(`[PendingTrips] ${msg}`, data ?? ''),
    warn: (msg, data) => process.env.NODE_ENV !== 'production' && console.warn(`[PendingTrips] ${msg}`, data ?? ''),
    error: (msg, data) => process.env.NODE_ENV !== 'production' && console.error(`[PendingTrips] ${msg}`, data ?? ''),
  };

  // Log initial trip counts once on mount
  useEffect(() => {
    logger.info('Driver trips loaded', { count: validDriverTrips.length });
    logger.info('Passenger trips loaded', { count: validPassengerTrips.length });
    logger.info('Raw pendingTripResultsRider structure:', pendingTripResultsRider);
    logger.info('Extracted pendingSeatRequests:', pendingTripResultsRider?.data?.pendingSeatRequests);

    // Validate the new data structure
    if (pendingTripResultsRider?.status === 'success' && pendingTripResultsRider?.data?.pendingSeatRequests) {
      logger.info('New JSON format detected successfully');
    } else if (Array.isArray(pendingTripResultsRider)) {
      logger.info('Legacy array format detected');
    } else {
      logger.warn('Unexpected data format for pendingTripResultsRider');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper to switch tabs with logging
  const switchTab = (tab) => {
    logger.info('Tab changed', { tab });
    setSelectedTab(tab);
  };

  // Function to handle the "More Details" button click for pending trips
  const handleMoreDetailsClick = async (trip) => {
    //logger.info('More Details clicked', trip);
    setPopupTripDetails(trip); // Set trip details directly
    setShowDetailsModal(true); // Open modal

    // Make API call to /optimizetrips for driver trips
    if (selectedTab === 'driver' && trip) {
      try {
        // Prepare the payload for the API call
        const payload = {
          trips_payload: {
            matching_trips: [
              {
                route_id: trip.route_id,
                driverPostsTrip_id: trip.driverPostsTrip_id,
                driverOrigin_latitude: trip.driverOrigin_latitude,
                driverOrigin_longitude: trip.driverOrigin_longitude,
                driverDestination_latitude: trip.driverDestination_latitude,
                driverDestination_longitude: trip.driverDestination_longitude,
                TripDeparture_DateTime: trip.TripDeparture_DateTime,
                TripArrival_DateTime: trip.TripArrival_DateTime
              }
            ],
            carpool_trips: pendingTripResultsDriver?.Existing_Carpools?.filter(
              carpool => carpool.driverPostsTrip_id === trip.driverPostsTrip_id
            ).map(carpool => ({
              carpool_id: carpool.carpool_id,
              driverPostsTrip_id: carpool.driverPostsTrip_id,
              WayPoint_latitude: carpool.WayPoint_latitude,
              WayPoint_longitude: carpool.WayPoint_longitude
            })) || []
          },
          pickup: {
            lat: trip.RiderPickupPtLatitude || trip.driverOrigin_latitude,
            lon: trip.RiderPickupPtLongitude || trip.driverOrigin_longitude
          },
          dropoff: {
            lat: trip.RiderDropoffPtLatitude || trip.driverDestination_latitude,
            lon: trip.RiderDropoffPtLongitude || trip.driverDestination_longitude
          },
          transportType: "Automobile"
        };

        logger.info('Calling /optimizetrips with payload:', payload);

        const response = await axios.post(`${API_URL}/optimizetrips`, payload);
        setOptimizeTripsResponse(response.data); // Store the response data in component state

        console.log('Optimize trips response:', response.data);

        // The optimizeTripsResponse state variable is now available throughout the component

      } catch (error) {
        logger.error('Error calling /optimizetrips:', error);
        // Optionally show error to user or handle silently
      }
    }
  };

  // Function to close the details popup
  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setPopupTripDetails(null); // Clear details when closing
  };

  // Function to handle driver accepting a pending trip request
  const handleAcceptRequest = async () => {
    if (!popupTripDetails || !optimizeTripsResponse) {
      logger.warn('Cannot accept request: missing trip details or optimization data');
      return;
    }

    try {
      setIsLoadingDecision(true); // Show loading screen

      // Prepare the payload for the /decisionpendingdriver API call
      const payload = {
        action: "yes",
        pendingReqId: popupTripDetails.pendingReqId,
        RiderUserId: popupTripDetails.RiderUserId,
        optimized_routes: optimizeTripsResponse.optimized_routes
      };

      //logger.info('Calling /decisionpendingdriver with payload:', payload);
      console.log(payload);

      const response = await axios.post(`${API_URL}/decisionpendingdriver`, payload);
      logger.info('Decision pending driver response:', response.data);

      // Show success alert and close modal
      setSuccessMessage("Request accepted successfully!");
      setShowSuccessAlert(true);
      closeDetailsModal();
      
      // Redirect to home after a short delay
      setTimeout(() => {
        navigate('/home');
      }, 2000);

    } catch (error) {
      logger.error('Error calling /decisionpendingdriver:', error);
      setErrorMessage("Error accepting request. Please try again.");
      setShowErrorAlert(true);
    } finally {
      setIsLoadingDecision(false); // Hide loading screen
    }
  };

  // Function to handle driver rejecting a pending trip request
  const handleRejectRequest = async () => {
    if (!popupTripDetails || !optimizeTripsResponse) {
      logger.warn('Cannot reject request: missing trip details or optimization data');
      return;
    }

    try {
      setIsLoadingDecision(true); // Show loading screen

      // Prepare the payload for the /decisionpendingdriver API call
      const payload = {
        action: "no",
        pendingReqId: popupTripDetails.pendingReqId,
        RiderUserId: popupTripDetails.RiderUserId,
        dropoff: {
          lat: popupTripDetails.RiderDropoffPtLatitude || popupTripDetails.driverDestination_latitude,
          lon: popupTripDetails.RiderDropoffPtLongitude || popupTripDetails.driverDestination_longitude
        },
        optimized_routes: [
          {
            driverPostsTrip_id: popupTripDetails.driverPostsTrip_id,
            route_id: popupTripDetails.route_id,
            ordered_stops: optimizeTripsResponse
          }
        ]
      };

      //logger.info('Calling /decisionpendingdriver with payload:', payload);
      console.log(payload);

      const response = await axios.post(`${API_URL}/decisionpendingdriver`, payload);
      //logger.info('Decision pending driver response:', response.data);

      // Show success alert and close modal
      setSuccessMessage("Request has been rejected");
      setShowSuccessAlert(true);
      closeDetailsModal();
      
      // Redirect to home after a short delay
      setTimeout(() => {
        navigate('/home');
      }, 2000);

    } catch (error) {
      logger.error('Error calling /decisionpendingdriver:', error);
      setErrorMessage("Error rejecting request. Please try again.");
      setShowErrorAlert(true);
    } finally {
      setIsLoadingDecision(false); // Hide loading screen
    }
  };

  // Function to handle rider canceling a seat request
  const handleCancelSeatRequest = async () => {
    if (!popupTripDetails || !popupTripDetails.pendingReqId) {
      logger.warn('Cannot cancel seat request: missing trip details or pendingReqId');
      return;
    }

    try {
      setIsLoadingRiderDecision(true); // Show loading screen

      // Prepare the payload for the /decisionpendingrider API call
      const payload = {
        PendingSeatReqID: popupTripDetails.pendingReqId
      };

      logger.info('Calling /decisionpendingrider with payload:', payload);
      console.log(payload);

      const response = await axios.post(`${API_URL}/decisionpendingrider`, payload);
      logger.info('Decision pending rider response:', response.data);

      // Show success alert and close modal
      setSuccessMessage("Seat request has been cancelled successfully!");
      setShowSuccessAlert(true);
      closeDetailsModal();
      
      // Redirect to home after a short delay
      setTimeout(() => {
        navigate('/home');
      }, 2000);

    } catch (error) {
      logger.error('Error calling /decisionpendingrider:', error);
      setErrorMessage("Error cancelling seat request. Please try again.");
      setShowErrorAlert(true);
    } finally {
      setIsLoadingRiderDecision(false); // Hide loading screen
    }
  };

  // Utility: format datetime strings into long-form local date using centralized utility
  const formatLocalDate = (ts) => {
    return formatDate(ts);
  };

  // Function to format numeric values safely
  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return 'N/A';
    const num = Number(value);
    if (Number.isNaN(num)) return 'N/A';
    return num.toFixed(decimals);
  };

  // Function to copy coordinates to clipboard
  const copyCoordinates = async (lat, lon) => {
    const coordinates = `${lat}, ${lon}`;
    try {
      await navigator.clipboard.writeText(coordinates);
      // You could add a toast notification here if needed
      console.log('Coordinates copied to clipboard:', coordinates);
    } catch (err) {
      console.error('Failed to copy coordinates:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = coordinates;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  // Function to combine optimized routes with existing carpools into one cohesive route
  const combineRouteWithCarpools = (optimizedRoutes, existingCarpools, tripId) => {
    if (!optimizedRoutes || !Array.isArray(optimizedRoutes) || optimizedRoutes.length === 0) {
      return null;
    }

    const route = optimizedRoutes[0]; // Get the first (and likely only) optimized route
    if (!route || !route.ordered_stops) {
      return null;
    }

    // Filter existing carpools for this specific trip
    const relevantCarpools = existingCarpools?.filter(carpool => 
      carpool.driverPostsTrip_id === tripId
    ) || [];

    // Create a map of carpool_id to carpool data for quick lookup
    const carpoolMap = {};
    relevantCarpools.forEach(carpool => {
      carpoolMap[carpool.carpool_id] = carpool;
    });

    // Process the ordered stops and enhance them with carpool information
    const enhancedStops = route.ordered_stops.map(stop => {
      const enhancedStop = { ...stop };
      
      // If this is a carpool stop with a carpool_id, add the carpool details
      if (stop.type === 'carpool' && stop.carpool_id && carpoolMap[stop.carpool_id]) {
        const carpoolData = carpoolMap[stop.carpool_id];
        enhancedStop.carpoolDetails = {
          wayPointName: carpoolData.WayPointName,
          wayPointLatitude: carpoolData.WayPoint_latitude,
          wayPointLongitude: carpoolData.WayPoint_longitude,
          carpoolId: carpoolData.carpool_id
        };
      }
      
      return enhancedStop;
    });

    return {
      ...route,
      ordered_stops: enhancedStops,
      totalStops: enhancedStops.length,
      totalDistance: enhancedStops[enhancedStops.length - 1]?.distanceMeters || 0,
      totalDuration: enhancedStops[enhancedStops.length - 1]?.etaFromOriginSeconds || 0
    };
  };

  // ---------------------------------------------------------------------------
  //                           Helper / UI handlers
  // ---------------------------------------------------------------------------

  return (
    <IonPage className="pending-trips-page">
      <IonContent fullscreen className="pending-trips-content">
        <div className="content-container">
          {/* Header with Back Button */}
          <div className="user-header">
            <IonButton
              fill="clear"
              className="back-button"
              onClick={() => navigate('/home')}
            >
              <IonIcon icon={arrowBackOutline} />
            </IonButton>
          </div>

          {/* Page Title */}
          <div className="page-title-section">
            <h2 className="page-title">Pending Trips</h2>
          </div>

          {/* Tab Segment */}
          <IonSegment
            value={selectedTab}
            onIonChange={e => switchTab(e.detail.value)}
            className="trips-segment"
          >
            <IonSegmentButton value="driver">
              <IonLabel>AS DRIVER</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="passenger">
              <IonLabel>AS PASSENGER</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {/* Trip Content */}
          <div className="tab-content-ionic">
            {selectedTab === 'driver' ? (
              // render pending trips as driver content here
              <IonList className="trip-list-ionic">
                {validDriverTrips.length > 0 ? (
                  validDriverTrips.map((trip, index) => (
                    <div 
                      key={index} 
                      className={`trip-card ${!trip.cancelled ? 'clickable-card' : ''}`}
                      onClick={!trip.cancelled ? () => handleMoreDetailsClick(trip) : undefined}
                    >
                      <div className="card-header">
                        <div className="driver-info passenger-driver-info">
                          <div className="trip-date-status">
                            <span className="trip-date">{formatLocalDate(trip.TripDeparture_DateTime)}</span>
                            <span className={`driver-tier ${trip.cancelled ? 'cancelled-tier' : 'pending-tier'}`}>
                              {trip.cancelled ? 'CANCELLED' : 'PENDING'}
                            </span>
                      </div>
                          <div className="driver-name-row">
                            <span className="driver-name">Rider: {trip.name || 'N/A'}</span>
                            <IonIcon icon={shield} className="shield-icon" />
                            <span className={`driver-tier ${trip.cancelled ? 'cancelled-tier' : 'verified-tier'}`}>Verified</span>
                        </div>
                        </div>
                        <span className="trip-price">
                          {trip.costOfLeg ? `$${trip.costOfLeg.toFixed(2)}` : '—'}
                        </span>
                      </div>

                      {/* Origin Stop */}
                      <div className="origin-stop">
                        <div className="origin-circle"></div>
                        <span className="origin-time">{formatTime(trip.TripDeparture_DateTime)}</span>
                        <span className="origin-location">{trip.driverOriginName || 'Driver Origin'}</span>
                            </div>

                      <div className="vertical-line"></div>

                      {/* Pickup Stop */}
                      <div className="pickup-stop">
                        <div className="pickup-circle"></div>
                        <span className="pickup-time">{formatTime(trip.TripDeparture_DateTime)}</span>
                        <span className="pickup-location">{trip.RiderPickupPtName || 'Rider Pickup'}</span>
                          </div>

                      <div className="vertical-line"></div>

                      {/* Dropoff Stop */}
                      <div className="dropoff-stop">
                        <div className="dropoff-triangle"></div>
                        <span className="dropoff-time">{formatTime(trip.TripArrival_DateTime)}</span>
                        <span className="dropoff-location">{trip.RiderDropoffPtName || 'Rider Drop-off'}</span>
                          </div>

                      <div className="vertical-line"></div>

                      {/* Destination Stop */}
                      <div className="destination-stop">
                        <div className="destination-circle"></div>
                        <span className="destination-time">{formatTime(trip.TripArrival_DateTime)}</span>
                        <span className="destination-location">{trip.driverDestinationName || 'Driver Destination'}</span>
                          </div>

                      <div className="separator"></div>

                      <div className="card-footer">
                        <div className="details-btn">
                          Details
                          <IonIcon icon={arrowBack} className="arrow" />
                            </div>
                          </div>
                    </div>
                  ))
                ) : (
                  <IonCard className="no-trips-card">
                    <IonCardContent>
                      <IonText className="no-trips-message">
                        <p>No pending trips found.</p>
                      </IonText>
                    </IonCardContent>
                  </IonCard>
                )}
              </IonList>
            ) : (
              // render pending trips as passenger content here
              <IonList className="trip-list-ionic">
                {validPassengerTrips.length > 0 ? (
                  validPassengerTrips.map((trip, index) => (
                    <div 
                      key={index} 
                      className={`trip-card ${!trip.cancelled ? 'clickable-card' : ''}`}
                      onClick={!trip.cancelled ? () => handleMoreDetailsClick(trip) : undefined}
                    >
                      <div className="card-header">
                        <div className="driver-info passenger-driver-info">
                          <div className="trip-date-status">
                            <span className="trip-date">{formatLocalDate(trip.RiderArrivalDate || trip.ArrivalAtPickup)}</span>
                            <span className={`driver-tier ${trip.cancelled ? 'cancelled-tier' : 'pending-tier'}`}>
                              {trip.cancelled ? 'CANCELLED' : 'PENDING'}
                            </span>
                      </div>
                          <div className="driver-name-row">
                            <span className="driver-name">Driver: {trip.driverName || 'N/A'}</span>
                            <IonIcon icon={shield} className="shield-icon" />
                            <span className={`driver-tier ${trip.cancelled ? 'cancelled-tier' : 'verified-tier'}`}>Verified</span>
                            </div>
                        </div>
                        <span className="trip-price">
                          {trip.costOfLeg ? `$${trip.costOfLeg.toFixed(2)}` : '—'}
                        </span>
                          </div>

                      {/* Start Location */}
                      <div className="origin-stop">
                        <div className="origin-circle"></div>
                        <span className="origin-time">
                          {trip.ArrivalAtPickup ? formatTime(trip.ArrivalAtPickup) : 'Time TBD'}
                        </span>
                        <span className="origin-location">{trip.RiderStartPtName || 'Start Location'}</span>
                                </div>
                      
                      <div className="vertical-line"></div>
                      
                      {/* Pickup Location */}
                      <div className="pickup-stop">
                        <div className="pickup-circle"></div>
                        <span className="pickup-time">
                          {trip.ArrivalAtPickup ? formatTime(trip.ArrivalAtPickup) : 'Time TBD'}
                        </span>
                        <span className="pickup-location">{trip.RiderPickupPtName || 'Pickup Location'}</span>
                            </div>
                      
                      <div className="vertical-line"></div>
                      
                      {/* Drop-off Location */}
                      <div className="dropoff-stop">
                        <div className="dropoff-triangle"></div>
                        <span className="dropoff-time">
                          {trip.ArrivalAtDropoff ? formatTime(trip.ArrivalAtDropoff) : 'Time TBD'}
                        </span>
                        <span className="dropoff-location">{trip.RiderDropoffPtName || 'Drop-off Location'}</span>
                          </div>

                      <div className="vertical-line"></div>
                      
                      {/* End Location */}
                      <div className="destination-stop">
                        <div className="destination-circle"></div>
                        <span className="destination-time">
                          {trip.ArrivalAtDropoff ? formatTime(trip.ArrivalAtDropoff) : 'Time TBD'}
                        </span>
                        <span className="destination-location">{trip.RiderEndPtName || 'End Location'}</span>
                          </div>

                      <div className="separator"></div>

                      <div className="card-footer">
                        <div className="details-btn">
                          Details
                          <IonIcon icon={arrowBack} className="arrow" />
                            </div>
                          </div>
                    </div>
                  ))
                ) : (
                  <IonCard className="no-trips-card">
                    <IonCardContent>
                      <IonText className="no-trips-message">
                        <p>No pending trips found.</p>
                      </IonText>
                    </IonCardContent>
                  </IonCard>
                )}
              </IonList>
            )}
          </div>
        </div>

        {/* Trip Details Modal */}
        <IonModal isOpen={showDetailsModal} onDidDismiss={closeDetailsModal} className="trip-details-modal">
          <IonHeader>
            <IonToolbar color="dark">
              <IonTitle>Trip Details</IonTitle>
              <IonButton
                fill="clear"
                slot="end"
                onClick={closeDetailsModal}
                className="modal-close-button"
              >
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonToolbar>
          </IonHeader>
          <IonContent scrollY={false}>
            <div className="trip-details-popup-content">
              {/* Conditional rendering based on selected tab and data format */}
              {selectedTab === 'driver' ? (
                // Render driver details with UpcomingTrips-style layout
                popupTripDetails && typeof popupTripDetails === 'object' ? (
                  <div className="trip-details-popup-container">
                    {/* Page Title */}
                    <div className="popup-title-section">
                      <h2 className="popup-title">Trip Details</h2>
                        </div>

                    <div className="popup-date-display">
                      {formatShortDate(popupTripDetails.TripDeparture_DateTime)}
                    </div>

                    {/* Price/Earnings Card */}
                    <div className="price-card">
                      <div className="price-details">
                        <span className="price-label">Potential Earnings</span>
                        <span className="price-value">
                            {popupTripDetails.costOfLeg !== undefined && popupTripDetails.costOfLeg !== null ?
                              `$${formatNumber(popupTripDetails.costOfLeg)}` : 'N/A'
                            }
                          </span>
                        </div>
                      </div>



                    {/* Route Card */}
                    <div className="route-card">
                          <div className="route-details-compact">
                        {/* Origin Stop */}
                        <div className="route-stop-compact">
                          <div className="origin-circle"></div>
                          <div className="stop-content-compact">
                            <span className="stop-label-compact">Origin</span>
                            <span className="stop-address-compact">
                              {popupTripDetails.driverOriginName || 'Driver Origin'}
                            </span>
                            <div className="arrival-time-compact">
                              <IonNote>{formatTime(popupTripDetails.TripDeparture_DateTime)}</IonNote>
                            </div>
                          </div>
                          <div className="stop-actions">
                            {popupTripDetails.driverOrigin_latitude && popupTripDetails.driverOrigin_longitude && (
                              <button 
                                className="copy-coordinates-btn"
                                onClick={() => copyCoordinates(popupTripDetails.driverOrigin_latitude, popupTripDetails.driverOrigin_longitude)}
                                title="Copy Coordinates"
                              >
                                <IonIcon icon={copyOutline} className="copy-icon" />
                              </button>
                            )}
                            {/* Navigation button to pickup */}
                            {popupTripDetails.RiderPickupPt_lat && popupTripDetails.RiderPickupPt_lon && (
                              <button 
                                className="maps-navigation-btn"
                                onClick={() => {
                                  const mapsUrl = `https://www.google.com/maps/dir/${popupTripDetails.driverOrigin_latitude},${popupTripDetails.driverOrigin_longitude}/${popupTripDetails.RiderPickupPt_lat},${popupTripDetails.RiderPickupPt_lon}`;
                                  window.open(mapsUrl, '_blank');
                                }}
                                title="Navigate to pickup"
                              >
                                <IonIcon icon={navigateOutline} className="maps-icon" />
                              </button>
                            )}
                          </div>
                        </div>
                        
                        <div className="vertical-line"></div>

                        {/* Pickup Stop */}
                        <div className="route-stop-compact">
                          <div className="pickup-circle"></div>
                          <div className="stop-content-compact">
                            <span className="stop-label-compact">Pickup</span>
                            <span className="stop-address-compact">
                              {popupTripDetails.RiderPickupPtName || 'Rider Pickup'}
                            </span>
                            <div className="arrival-time-compact">
                              <IonNote>{formatTime(popupTripDetails.TripDeparture_DateTime)}</IonNote>
                            </div>
                          </div>
                          <div className="stop-actions">
                            {popupTripDetails.RiderPickupPt_lat && popupTripDetails.RiderPickupPt_lon && (
                              <button 
                                className="copy-coordinates-btn"
                                onClick={() => copyCoordinates(popupTripDetails.RiderPickupPt_lat, popupTripDetails.RiderPickupPt_lon)}
                                title="Copy Coordinates"
                              >
                                <IonIcon icon={copyOutline} className="copy-icon" />
                              </button>
                            )}
                            {/* Navigation button to dropoff */}
                            {popupTripDetails.RiderDropoffPt_lat && popupTripDetails.RiderDropoffPt_lon && (
                              <button 
                                className="maps-navigation-btn"
                                onClick={() => {
                                  const mapsUrl = `https://www.google.com/maps/dir/${popupTripDetails.RiderPickupPt_lat},${popupTripDetails.RiderPickupPt_lon}/${popupTripDetails.RiderDropoffPt_lat},${popupTripDetails.RiderDropoffPt_lon}`;
                                  window.open(mapsUrl, '_blank');
                                }}
                                title="Navigate to dropoff"
                              >
                                <IonIcon icon={navigateOutline} className="maps-icon" />
                              </button>
                            )}
                          </div>
                        </div>
                        
                        <div className="vertical-line"></div>

                        {/* Dropoff Stop */}
                        <div className="route-stop-compact">
                          <div className="dropoff-triangle"></div>
                          <div className="stop-content-compact">
                            <span className="stop-label-compact">Drop-off</span>
                            <span className="stop-address-compact">
                              {popupTripDetails.RiderDropoffPtName || 'Rider Drop-off'}
                            </span>
                            <div className="arrival-time-compact">
                              <IonNote>{formatTime(popupTripDetails.TripArrival_DateTime)}</IonNote>
                            </div>
                          </div>
                          <div className="stop-actions">
                            {popupTripDetails.RiderDropoffPt_lat && popupTripDetails.RiderDropoffPt_lon && (
                              <button 
                                className="copy-coordinates-btn"
                                onClick={() => copyCoordinates(popupTripDetails.RiderDropoffPt_lat, popupTripDetails.RiderDropoffPt_lon)}
                                title="Copy Coordinates"
                              >
                                <IonIcon icon={copyOutline} className="copy-icon" />
                              </button>
                            )}
                            {/* Navigation button to destination */}
                            {popupTripDetails.driverDestination_latitude && popupTripDetails.driverDestination_longitude && (
                              <button 
                                className="maps-navigation-btn"
                                onClick={() => {
                                  const mapsUrl = `https://www.google.com/maps/dir/${popupTripDetails.RiderDropoffPt_lat},${popupTripDetails.RiderDropoffPt_lon}/${popupTripDetails.driverDestination_latitude},${popupTripDetails.driverDestination_longitude}`;
                                  window.open(mapsUrl, '_blank');
                                }}
                                title="Navigate to destination"
                              >
                                <IonIcon icon={navigateOutline} className="maps-icon" />
                              </button>
                            )}
                          </div>
                        </div>
                        
                        <div className="vertical-line"></div>

                        {/* Destination Stop */}
                        <div className="route-stop-compact">
                          <div className="destination-circle"></div>
                          <div className="stop-content-compact">
                            <span className="stop-label-compact">Destination</span>
                            <span className="stop-address-compact">
                              {popupTripDetails.driverDestinationName || 'Driver Destination'}
                            </span>
                            <div className="arrival-time-compact">
                              <IonNote>{formatTime(popupTripDetails.TripArrival_DateTime)}</IonNote>
                            </div>
                          </div>
                          <div className="stop-actions">
                            {popupTripDetails.driverDestination_latitude && popupTripDetails.driverDestination_longitude && (
                              <button 
                                className="copy-coordinates-btn"
                                onClick={() => copyCoordinates(popupTripDetails.driverDestination_latitude, popupTripDetails.driverDestination_longitude)}
                                title="Copy Coordinates"
                              >
                                <IonIcon icon={copyOutline} className="copy-icon" />
                              </button>
                            )}
                            {/* No navigation button for destination - it's the last stop */}
                          </div>
                        </div>
                      </div>
                          </div>
                          
                    {/* Trip Statistics Card */}
                    <div className="stats-card">
                      <div className="stats-grid">
                        <div className="stat-item">
                          <span className="stat-label">Rider Name</span>
                          <span className="stat-value">{popupTripDetails.name || 'N/A'}</span>
                                    </div>
                        <div className="stat-item">
                          <span className="stat-label">Status</span>
                          <span className="stat-value">Pending</span>
                                  </div>
                                </div>
                    </div>

                      {/* Accept/Reject Actions */}
                      {!popupTripDetails.AnswerFlag && !popupTripDetails.cancelled && (
                        <div className="modal-actions-section-compact" style={{ marginTop: '24px' }}>
                          <IonButton
                            className="search-trip-button-compact"
                            onClick={handleAcceptRequest}
                            disabled={isLoadingDecision}
                          >
                            <IonIcon icon={checkmarkOutline} slot="start" />
                            {isLoadingDecision ? 'Processing...' : 'Accept Request'}
                          </IonButton>
                          <IonButton
                            fill="outline"
                            className="cancel-button-compact"
                            onClick={handleRejectRequest}
                            disabled={isLoadingDecision}
                          >
                            <IonIcon icon={closeCircleOutline} slot="start" />
                            Reject Request
                          </IonButton>
                        </div>
                      )}
                  </div>
                ) : popupTripDetails === null ? (
                  <div className="loading-container">
                    <IonSpinner name="crescent" color="dark" />
                    <IonText>
                      <p>Loading driver details...</p>
                    </IonText>
                  </div>
                ) : (
                  <IonCard>
                    <IonCardContent>
                      <IonText color="danger">
                        <p>Error loading driver details. Unexpected data format.</p>
                      </IonText>
                    </IonCardContent>
                  </IonCard>
                )
              ) : (
                // Render passenger details with UpcomingTrips-style layout
                popupTripDetails && typeof popupTripDetails === 'object' ? (
                  <div className="trip-details-popup-container">
                    {/* Page Title */}
                    <div className="popup-title-section">
                      <h2 className="popup-title">Trip Details</h2>
                    </div>

                    <div className="popup-date-display">
                      {formatShortDate(popupTripDetails.RiderArrivalDate || popupTripDetails.ArrivalAtPickup)}
                    </div>

                    {/* Driver Card */}
                    <div className="driver-card">
                      <div className="driver-info-compact">
                        <IonAvatar className="driver-avatar-compact">
                          <img
                            src="/default-avatar.png"
                            alt="Driver"
                            crossOrigin="anonymous"
                          />
                        </IonAvatar>
                        <div className="driver-details-compact">
                          <span className="driver-name-compact">
                            {popupTripDetails.driverName || 'N/A'}
                          </span>
                          <IonIcon icon={shield} className="shield-icon-compact" />
                          <span className="driver-tier-compact">Verified</span>
                        </div>
                      </div>
                    </div>

                    {/* Price Card */}
                    <div className="price-card">
                      <div className="price-details">
                        <span className="price-label">Price</span>
                        <span className="price-value">
                            {popupTripDetails.costOfLeg !== undefined && popupTripDetails.costOfLeg !== null ?
                              `$${formatNumber(popupTripDetails.costOfLeg)}` : 'N/A'
                            }
                          </span>
                        </div>
                      </div>

                    {/* Route Card */}
                    <div className="route-card">
                      <div className="route-details-compact">
                        {/* Start */}
                        <div className="route-stop-compact">
                          <div className="origin-circle"></div>
                          <div className="stop-content-compact">
                            <span className="stop-label-compact">Start</span>
                            <span className="stop-address-compact">
                              {popupTripDetails.RiderStartPtName || 'Start Location'}
                            </span>
                            <div className="arrival-time-compact">
                              <IonNote>
                                {popupTripDetails.ArrivalAtPickup ? 
                                  `Depart by: ${formatTime(popupTripDetails.ArrivalAtPickup)}` : 
                                  'Depart by: Time TBD'
                                }
                                </IonNote>
                            </div>
                          </div>
                          <div className="stop-actions">
                            {popupTripDetails.RiderStartPt_lat && popupTripDetails.RiderStartPt_lon && (
                              <button 
                                className="copy-coordinates-btn"
                                onClick={() => copyCoordinates(popupTripDetails.RiderStartPt_lat, popupTripDetails.RiderStartPt_lon)}
                                title="Copy Coordinates"
                              >
                                <IonIcon icon={copyOutline} className="copy-icon" />
                              </button>
                            )}
                            {/* Navigation button to pickup */}
                            {popupTripDetails.RiderPickupPt_lat && popupTripDetails.RiderPickupPt_lon && (
                              <button 
                                className="maps-navigation-btn"
                                onClick={() => {
                                  const mapsUrl = `https://www.google.com/maps/dir/${popupTripDetails.RiderStartPt_lat},${popupTripDetails.RiderStartPt_lon}/${popupTripDetails.RiderPickupPt_lat},${popupTripDetails.RiderPickupPt_lon}`;
                                  window.open(mapsUrl, '_blank');
                                }}
                                title="Navigate to pickup"
                              >
                                <IonIcon icon={navigateOutline} className="maps-icon" />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="vertical-line"></div>

                        {/* Pickup */}
                        <div className="route-stop-compact">
                          <div className="pickup-circle"></div>
                          <div className="stop-content-compact">
                            <span className="stop-label-compact">Pickup</span>
                            <span className="stop-address-compact">
                              {popupTripDetails.RiderPickupPtName || 'Pickup Location'}
                            </span>
                            <div className="arrival-time-compact">
                              <IonNote>
                                {popupTripDetails.ArrivalAtPickup ? 
                                  `Arrive by: ${formatTime(popupTripDetails.ArrivalAtPickup)}` : 
                                  'Arrive by: Time TBD'
                                }
                              </IonNote>
                            </div>
                          </div>
                          <div className="stop-actions">
                            {popupTripDetails.RiderPickupPt_lat && popupTripDetails.RiderPickupPt_lon && (
                              <button 
                                className="copy-coordinates-btn"
                                onClick={() => copyCoordinates(popupTripDetails.RiderPickupPt_lat, popupTripDetails.RiderPickupPt_lon)}
                                title="Copy Coordinates"
                              >
                                <IonIcon icon={copyOutline} className="copy-icon" />
                              </button>
                            )}
                            {/* Navigation button to dropoff */}
                            {popupTripDetails.RiderDropoffPt_lat && popupTripDetails.RiderDropoffPt_lon && (
                              <button 
                                className="maps-navigation-btn"
                                onClick={() => {
                                  const mapsUrl = `https://www.google.com/maps/dir/${popupTripDetails.RiderPickupPt_lat},${popupTripDetails.RiderPickupPt_lon}/${popupTripDetails.RiderDropoffPt_lat},${popupTripDetails.RiderDropoffPt_lon}`;
                                  window.open(mapsUrl, '_blank');
                                }}
                                title="Navigate to dropoff"
                              >
                                <IonIcon icon={navigateOutline} className="maps-icon" />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="vertical-line"></div>

                        {/* Drop-off */}
                        <div className="route-stop-compact">
                          <div className="dropoff-triangle"></div>
                          <div className="stop-content-compact">
                            <span className="stop-label-compact">Drop-off</span>
                            <span className="stop-address-compact">
                              {popupTripDetails.RiderDropoffPtName || 'Drop-off Location'}
                            </span>
                            <div className="arrival-time-compact">
                              <IonNote>
                                {popupTripDetails.ArrivalAtDropoff ? 
                                  `Arrive by: ${formatTime(popupTripDetails.ArrivalAtDropoff)}` : 
                                  'Arrive by: Time TBD'
                                }
                              </IonNote>
                            </div>
                          </div>
                          <div className="stop-actions">
                            {popupTripDetails.RiderDropoffPt_lat && popupTripDetails.RiderDropoffPt_lon && (
                              <button 
                                className="copy-coordinates-btn"
                                onClick={() => copyCoordinates(popupTripDetails.RiderDropoffPt_lat, popupTripDetails.RiderDropoffPt_lon)}
                                title="Copy Coordinates"
                              >
                                <IonIcon icon={copyOutline} className="copy-icon" />
                              </button>
                            )}
                            {/* Navigation button to destination */}
                            {popupTripDetails.RiderEndPt_lat && popupTripDetails.RiderEndPt_lon && (
                              <button 
                                className="maps-navigation-btn"
                                onClick={() => {
                                  const mapsUrl = `https://www.google.com/maps/dir/${popupTripDetails.RiderDropoffPt_lat},${popupTripDetails.RiderDropoffPt_lon}/${popupTripDetails.RiderEndPt_lat},${popupTripDetails.RiderEndPt_lon}`;
                                  window.open(mapsUrl, '_blank');
                                }}
                                title="Navigate to destination"
                              >
                                <IonIcon icon={navigateOutline} className="maps-icon" />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="vertical-line"></div>

                        {/* Destination */}
                        <div className="route-stop-compact">
                          <div className="destination-circle"></div>
                          <div className="stop-content-compact">
                            <span className="stop-label-compact">Destination</span>
                            <span className="stop-address-compact">
                              {popupTripDetails.RiderEndPtName || 'End Location'}
                            </span>
                            <div className="arrival-time-compact">
                              <IonNote>
                                {popupTripDetails.ArrivalAtDropoff ? 
                                  `Arrive by: ${formatTime(popupTripDetails.ArrivalAtDropoff)}` : 
                                  'Arrive by: Time TBD'
                                }
                                </IonNote>
                              </div>
                            </div>
                          <div className="stop-actions">
                            {popupTripDetails.RiderEndPt_lat && popupTripDetails.RiderEndPt_lon && (
                              <button 
                                className="copy-coordinates-btn"
                                onClick={() => copyCoordinates(popupTripDetails.RiderEndPt_lat, popupTripDetails.RiderEndPt_lon)}
                                title="Copy Coordinates"
                              >
                                <IonIcon icon={copyOutline} className="copy-icon" />
                              </button>
                            )}
                            {/* No navigation button for destination - it's the last stop */}
                          </div>
                        </div>
                        </div>
                      </div>

                    {/* Trip Statistics Card */}
                    <div className="stats-card">
                      <div className="stats-grid">
                        <div className="stat-item">
                          <span className="stat-label">Status</span>
                          <span className="stat-value">Pending</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Request ID</span>
                          <span className="stat-value">{popupTripDetails.pendingReqId || 'N/A'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Cancel Action */}
                      <div className="cancel-button-container">
                        <IonButton
                          expand="block"
                          className="cancel-button-popup"
                          onClick={handleCancelSeatRequest}
                          disabled={isLoadingRiderDecision}
                        >
                          {isLoadingRiderDecision ? (
                            <>
                              <IonSpinner name="crescent" slot="start" />
                              Cancelling...
                            </>
                          ) : (
                            <>
                              <IonIcon icon={alertCircleOutline} slot="start" />
                              CANCEL SEAT REQUEST
                            </>
                          )}
                        </IonButton>
                      </div>
                  </div>
                ) : popupTripDetails === null ? (
                  <div className="loading-container">
                    <IonSpinner name="crescent" color="dark" />
                    <IonText>
                      <p>Loading passenger details...</p>
                    </IonText>
                  </div>
                ) : (
                  <IonCard>
                    <IonCardContent>
                      <IonText color="danger">
                        <p>Error loading passenger details. Unexpected data format.</p>
                      </IonText>
                    </IonCardContent>
                  </IonCard>
                )
              )}
            </div>
          </IonContent>
        </IonModal>

        {/* Success Alert */}
        <IonAlert
          isOpen={showSuccessAlert}
          onDidDismiss={() => setShowSuccessAlert(false)}
          header="Success!"
          message={successMessage}
          buttons={['OK']}
        />

        {/* Error Alert */}
        <IonAlert
          isOpen={showErrorAlert}
          onDidDismiss={() => setShowErrorAlert(false)}
          header="Error"
          message={errorMessage}
          buttons={['OK']}
        />

        {/* Loading Screen */}
        <IonLoading
          isOpen={isLoadingDecision || isLoadingRiderDecision}
          message={isLoadingDecision ? "Processing your decision..." : "Cancelling seat request..."}
          spinner="crescent"
        />
      </IonContent>
    </IonPage>
  );
};

export default PendingTrips;

